import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import ChatKitDock from './components/ChatKitDock.jsx';
import { FeatureFlagProvider } from './context/FeatureFlagProvider.jsx';
import './index.css';
import { initAnalytics } from './lib/analytics.js';
import { getApiOrigin } from './lib/apiBase.js';

const AUTH_TOKEN_KEY = 'claimsupp_token';

function installAuthFetch() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (window.__claimsuppAuthFetchInstalled) return;

  let apiOrigin = '';
  try {
    apiOrigin = new URL(getApiOrigin()).origin;
  } catch {
    apiOrigin = window.location.origin;
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    let requestUrl = '';
    if (typeof input === 'string' || input instanceof URL) {
      requestUrl = String(input);
    } else if (input && typeof input.url === 'string') {
      requestUrl = input.url;
    }

    let resolved = null;
    try {
      resolved = new URL(requestUrl, window.location.origin);
    } catch {
      resolved = null;
    }

    if (!resolved || resolved.origin !== apiOrigin) {
      return originalFetch(input, init);
    }

    const headers = new Headers(
      init.headers || (input instanceof Request ? input.headers : undefined),
    );
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (token) headers.set('Authorization', `Bearer ${token}`);
    } catch {
      // ignore storage failures
    }

    const nextInit = {
      ...init,
      headers,
      credentials: init.credentials || 'include',
    };
    return originalFetch(input, nextInit);
  };

  window.__claimsuppAuthFetchInstalled = true;
}

const queryClient = new QueryClient();
initAnalytics();
installAuthFetch();

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <FeatureFlagProvider>
            <>
              <App />
              <ChatKitDock />
            </>
          </FeatureFlagProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
